/**
 * Utility functions for parsing and serializing theory content blocks
 * 
 * Block Types:
 * - heading: Section heading (head:...:head)
 * - paragraph: Plain text paragraph
 * - list: Bullet list with multiple items (ul:...:ul)
 * - image: Image URL (img:...)
 * - code: Code block (go:...:go)
 * - table: JSON table data (table:...:table)
 */

// Block type constants
export const BLOCK_TYPES = {
  HEADING: "heading",
  PARAGRAPH: "paragraph",
  LIST: "list",
  IMAGE: "image",
  CODE: "code",
  TABLE: "table",
};

/**
 * Create a new empty block of the specified type
 */
export const createEmptyBlock = (type) => {
  const id = `block-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

  switch (type) {
    case BLOCK_TYPES.HEADING:
      return { id, type, value: "" };
    case BLOCK_TYPES.PARAGRAPH:
      return { id, type, value: "" };
    case BLOCK_TYPES.LIST:
      return { id, type, items: [""] };
    case BLOCK_TYPES.IMAGE:
      return { id, type, url: "" };
    case BLOCK_TYPES.CODE:
      return { id, type, value: "" };
    case BLOCK_TYPES.TABLE:
      // Default 2x2 table including header
      // Rows are arrays of strings
      return {
        id,
        type,
        rows: [
          ["Header 1", "Header 2"],
          ["Cell 1", "Cell 2"]
        ]
      };
    default:
      return { id, type: BLOCK_TYPES.PARAGRAPH, value: "" };
  }
};

/**
 * Parse theoryContent string array into structured blocks
 * This reverses the serialization process to load existing content
 */
export const parseTheoryContent = (theoryContentArray) => {
  if (!Array.isArray(theoryContentArray) || theoryContentArray.length === 0) {
    return [];
  }

  const blocks = [];

  theoryContentArray.forEach((content) => {
    if (!content || typeof content !== "string") return;

    const trimmed = content.trim();
    if (!trimmed) return;

    // Check for image block
    if (trimmed.startsWith("img:")) {
      blocks.push({
        id: `block-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: BLOCK_TYPES.IMAGE,
        url: trimmed.slice(4).trim(),
      });
      return;
    }

    // Check for table block
    if (trimmed.startsWith("table:") && trimmed.endsWith(":table")) {
      try {
        const jsonStr = trimmed.slice(6, -6).trim();
        const rows = JSON.parse(jsonStr);
        if (Array.isArray(rows)) {
          blocks.push({
            id: `block-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            type: BLOCK_TYPES.TABLE,
            rows: rows,
          });
        }
      } catch (e) {
        console.warn("Failed to parse table block:", e);
      }
      return;
    }

    // Check for heading block
    if (trimmed.startsWith("head:") && trimmed.endsWith(":head")) {
      blocks.push({
        id: `block-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: BLOCK_TYPES.HEADING,
        value: trimmed.slice(5, -5).trim(),
      });
      return;
    }

    // Check for code block (single line or multi-line)
    if (trimmed.startsWith("go:") && trimmed.endsWith(":go")) {
      blocks.push({
        id: `block-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: BLOCK_TYPES.CODE,
        value: trimmed.slice(3, -3).trim(),
      });
      return;
    }

    // Check for list items (may be combined with other content in same string)
    // Parse line by line for mixed content
    const lines = content.split("\n");
    let currentListItems = [];
    let currentTextLines = [];

    const flushText = () => {
      if (currentTextLines.length > 0) {
        const text = currentTextLines.join("\n").trim();
        if (text) {
          blocks.push({
            id: `block-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            type: BLOCK_TYPES.PARAGRAPH,
            value: text,
          });
        }
        currentTextLines = [];
      }
    };

    const flushList = () => {
      if (currentListItems.length > 0) {
        blocks.push({
          id: `block-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          type: BLOCK_TYPES.LIST,
          items: [...currentListItems],
        });
        currentListItems = [];
      }
    };

    let isInCodeBlock = false;
    let codeLines = [];

    for (const line of lines) {
      const t = line.trim();

      // Handle multi-line code blocks
      if (isInCodeBlock) {
        if (t.endsWith(":go")) {
          codeLines.push(t.slice(0, -3));
          flushText();
          flushList();
          blocks.push({
            id: `block-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            type: BLOCK_TYPES.CODE,
            value: codeLines.join("\n").trim(),
          });
          codeLines = [];
          isInCodeBlock = false;
        } else {
          codeLines.push(line);
        }
        continue;
      }

      // Start of multi-line code block
      if (t.startsWith("go:") && !t.endsWith(":go")) {
        flushText();
        flushList();
        isInCodeBlock = true;
        codeLines.push(t.slice(3));
        continue;
      }

      // Single line code
      if (t.startsWith("go:") && t.endsWith(":go")) {
        flushText();
        flushList();
        blocks.push({
          id: `block-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          type: BLOCK_TYPES.CODE,
          value: t.slice(3, -3).trim(),
        });
        continue;
      }

      // Image
      if (t.startsWith("img:")) {
        flushText();
        flushList();
        blocks.push({
          id: `block-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          type: BLOCK_TYPES.IMAGE,
          url: t.slice(4).trim(),
        });
        continue;
      }

      // Table
      if (t.startsWith("table:") && t.endsWith(":table")) {
        flushText();
        flushList();
        try {
          const jsonStr = t.slice(6, -6).trim();
          const rows = JSON.parse(jsonStr);
          if (Array.isArray(rows)) {
            blocks.push({
              id: `block-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
              type: BLOCK_TYPES.TABLE,
              rows: rows,
            });
          }
        } catch (e) {
          console.warn("Failed to parse table line:", e);
        }
        continue;
      }


      // Heading
      if (t.startsWith("head:") && t.endsWith(":head")) {
        flushText();
        flushList();
        blocks.push({
          id: `block-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          type: BLOCK_TYPES.HEADING,
          value: t.slice(5, -5).trim(),
        });
        continue;
      }

      // List item
      if (t.startsWith("ul:") && t.endsWith(":ul")) {
        flushText();
        currentListItems.push(t.slice(3, -3).trim());
        continue;
      }

      // Blank line - flush buffers
      if (!t) {
        flushList();
        flushText();
        continue;
      }

      // Regular text
      flushList();
      currentTextLines.push(line);
    }

    // Flush remaining content
    flushList();
    flushText();
  });

  return blocks;
};

/**
 * Serialize structured blocks back to theoryContent string array
 * Each block becomes one element in the array
 */
export const serializeTheoryBlocks = (blocks) => {
  if (!Array.isArray(blocks) || blocks.length === 0) {
    return [];
  }

  return blocks
    .map((block) => {
      switch (block.type) {
        case BLOCK_TYPES.HEADING:
          if (!block.value?.trim()) return null;
          return `head:${block.value.trim()}:head`;

        case BLOCK_TYPES.PARAGRAPH:
          if (!block.value?.trim()) return null;
          return block.value.trim();

        case BLOCK_TYPES.LIST:
          if (!block.items || block.items.length === 0) return null;
          const validItems = block.items.filter((item) => item?.trim());
          if (validItems.length === 0) return null;
          return validItems.map((item) => `ul:${item.trim()}:ul`).join("\n");

        case BLOCK_TYPES.IMAGE:
          if (!block.url?.trim()) return null;
          return `img:${block.url.trim()}`;

        case BLOCK_TYPES.CODE:
          if (!block.value?.trim()) return null;
          return `go:\n${block.value.trim()}\n:go`;

        case BLOCK_TYPES.TABLE:
          if (!block.rows || !Array.isArray(block.rows) || block.rows.length === 0) return null;
          try {
            const jsonStr = JSON.stringify(block.rows);
            return `table:${jsonStr}:table`;
          } catch {
            return null;
          }

        default:
          return null;
      }
    })
    .filter(Boolean);
};
